import React from "react";
import { primaryText, secondaryText, greyTextColor, textBlue, borderPrimary } from "../../../../../styling/stylingHelper";
import { eur } from "../../../../orderhistory/utils";
import { OverrideTag, OverrideResetButton } from "./ChoiceOverrideControls";

const DeleteIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.5 5H4.16667H17.5" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6.66699 5V3.33333C6.66699 2.89131 6.84259 2.46738 7.15515 2.15482C7.46771 1.84226 7.89164 1.66667 8.33366 1.66667H11.667C12.109 1.66667 12.5329 1.84226 12.8455 2.15482C13.158 2.46738 13.3337 2.89131 13.3337 3.33333V5M15.8337 5V16.6667C15.8337 17.1087 15.6581 17.5326 15.3455 17.8452C15.033 18.1577 14.609 18.3333 14.167 18.3333H5.83366C5.39164 18.3333 4.96771 18.1577 4.65515 17.8452C4.34259 17.5326 4.16699 17.1087 4.16699 16.6667V5H15.8337Z" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M8.33301 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M11.667 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

interface NestedMenuItem {
    name: string;
    quantityNeeded: number;
    totalCost: number;
    nestedCostPriceId: string;
}

interface NestedMenuItemsSectionProps {
    menuItems: NestedMenuItem[];
    hasSubrecipes: boolean;
    inlineQuantities: Record<string, string>;
    confirmingDeleteId: string | null;
    onQuantityChange: (id: string, value: string) => void;
    onSaveEdit: (id: string, qty: string, originalQty: number) => void;
    onDelete: (id: string) => void;
    onConfirmDelete: (id: string, name: string) => void;
    onClearAll?: () => void;
    // BYO dish context: nestedCostPriceIds whose quantity is a per-dish
    // override, with a reset back to the shared value. Absent outside dish context.
    overriddenKeys?: Set<string>;
    onResetOverride?: (id: string) => void;
}

const NestedMenuItemsSection: React.FC<NestedMenuItemsSectionProps> = ({
    menuItems, hasSubrecipes, inlineQuantities, confirmingDeleteId,
    onQuantityChange, onSaveEdit, onDelete, onConfirmDelete, onClearAll,
    overriddenKeys, onResetOverride
}) => {
    if (menuItems.length === 0) return null;

    return (
        <div className={`rounded-xl border ${borderPrimary} bg-background dark:bg-background-dark p-4 ${hasSubrecipes ? 'mt-5' : ''}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <img src="/assets/sub-recipes.svg" alt="menu-items" />
                    <span className={`text-[16px] leading-5 font-semibold ${primaryText}`}>Menu Items</span>
                </div>
                {onClearAll && (
                    <button type="button" onClick={onClearAll} className="p-1 hover:opacity-70 transition-opacity">
                        <DeleteIcon />
                    </button>
                )}
            </div>

            <div className="space-y-3 pt-4">
                {menuItems.map((menuItem) => {
                    const menuItemUnitPrice = menuItem.totalCost / menuItem.quantityNeeded;
                    const currentQtyStr = inlineQuantities[menuItem.nestedCostPriceId] ?? menuItem.quantityNeeded.toString();
                    const currentQty = parseFloat(currentQtyStr) || 0;
                    const estimatedCost = menuItemUnitPrice * currentQty;

                    return (
                        <div key={menuItem.nestedCostPriceId} className={`rounded-lg border py-[11px] pl-3 pr-4 flex flex-col bg-backgroundSecondary dark:bg-backgroundSecondary-dark ${borderPrimary}`}>
                            <div className="flex justify-between items-start mb-2">
                                <p className={`text-[16px] leading-6 font-semibold tracking-[-0.16px] ${primaryText}`}>
                                    {menuItem.name}
                                </p>
                                <div className="flex items-center gap-2">
                                    {confirmingDeleteId === menuItem.nestedCostPriceId ? (
                                        <button type="button" onClick={() => onConfirmDelete(menuItem.nestedCostPriceId, menuItem.name)} className="h-8 px-3 text-[14px] font-semibold bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white dark:text-white rounded-lg transition-colors">
                                            Remove
                                        </button>
                                    ) : (
                                        <button type="button" onClick={() => onDelete(menuItem.nestedCostPriceId)} className="p-1 hover:opacity-70 transition-opacity">
                                            <DeleteIcon />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center justify-between gap-3">
                                <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>Unit Price</p>
                                <p className={`${secondaryText} text-[14px] leading-5 font-medium`}>
                                    {eur(menuItemUnitPrice)}/portion
                                </p>
                            </div>

                            {/* Quantity (tag + reset appear when this dish overrides the shared value) */}
                            <div className="flex items-center justify-between gap-3 mt-2">
                                <div className="flex items-center gap-1.5">
                                    <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>Quantity</p>
                                    {overriddenKeys?.has(menuItem.nestedCostPriceId) && <OverrideTag />}
                                </div>
                                <div className="flex items-center gap-1">
                                    {overriddenKeys?.has(menuItem.nestedCostPriceId) && onResetOverride && (
                                        <OverrideResetButton onClick={() => onResetOverride(menuItem.nestedCostPriceId)} />
                                    )}
                                    <input
                                        type="number" inputMode="decimal" min={0} step="any"
                                        value={currentQtyStr}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) onQuantityChange(menuItem.nestedCostPriceId, value);
                                        }}
                                        onBlur={() => onSaveEdit(menuItem.nestedCostPriceId, currentQtyStr, menuItem.quantityNeeded)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                        className="w-16 text-right bg-white dark:bg-background-dark border border-borderGrey dark:border-borderGrey-dark rounded px-2 py-0.5 text-[14px] leading-5 font-medium text-[#0C111D] dark:text-textPrimary-dark outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    />
                                    <span className={`text-[14px] leading-5 font-medium ${secondaryText}`}>portions</span>
                                </div>
                            </div>

                            <div className="flex justify-between items-center gap-2 mt-2">
                                <p className={`text-[14px] leading-[21px] font-normal tracking-[-0.14px] ${greyTextColor}`}>Cost</p>
                                <p className={`text-[14px] leading-5 font-medium ${textBlue}`}>{eur(estimatedCost)}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default NestedMenuItemsSection;
